import { useNavigate, useParams, Link } from 'react-router-dom';
import { useProject, useUpdateProject } from '../../hooks/useProjects';
import { ProjectForm } from '../../components/projects/ProjectForm';
import type { UpdateProjectRequest } from '../../types/project';
import './ProjectFormPage.css';

export function EditProjectPage() {
  const navigate = useNavigate();
  const { projectId } = useParams<{ projectId: string }>();
  const { data: project, isLoading, error } = useProject(projectId);
  const updateProject = useUpdateProject();

  const handleSubmit = async (data: UpdateProjectRequest) => {
    if (!projectId) return;

    await updateProject.mutateAsync({
      projectId,
      data,
    });
    navigate(`/projects/${projectId}`);
  };

  const handleCancel = () => {
    navigate(`/projects/${projectId}`);
  };

  if (isLoading) {
    return (
      <div className="project-form-page">
        <div className="project-form-page__loading">
          <div className="spinner" />
          <p>Loading project...</p>
        </div>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="project-form-page">
        <div className="project-form-page__error">
          <p>Failed to load project</p>
          <p className="error-detail">{error?.message || 'Project not found'}</p>
          <button className="btn btn--secondary" onClick={() => navigate(-1)}>
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="project-form-page">
      <header className="project-form-page__header">
        <div className="project-form-page__breadcrumb">
          <Link to={`/organizations/${project.organization_id}/projects`}>Projects</Link>
          <span>/</span>
          <Link to={`/projects/${project.id}`}>{project.name}</Link>
          <span>/</span>
          <span>Edit</span>
        </div>
        <h1>Edit Project</h1>
        <p className="project-form-page__subtitle">
          Update your project's name and description.
        </p>
      </header>

      <div className="project-form-page__content">
        <ProjectForm
          initialData={{
            name: project.name,
            description: project.description || '',
          }}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          isLoading={updateProject.isPending}
          error={updateProject.error?.message}
          isEditing
        />
      </div>
    </div>
  );
}
